<script setup lang="ts">
import { onErrorCaptured, ref } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * Catches render/setup errors in its default slot. Special-cases async chunk
 * load failures (which happen when a deploy lands mid-session) by suggesting
 * a reload.
 *
 * Wrap `<RouterView />` in App.vue to protect the whole app.
 */
const { t } = useI18n()
const error = ref<Error | null>(null)

const isChunkLoadError = (e: Error) =>
  /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
    e.message,
  )

onErrorCaptured((err) => {
  error.value = err instanceof Error ? err : new Error(String(err))
  console.error('[ErrorBoundary]', err)
  return false
})

function reset() {
  error.value = null
}

function reload() {
  window.location.reload()
}
</script>

<template>
  <slot v-if="!error" />
  <div
    v-else
    class="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center"
  >
    <h2 class="text-xl font-semibold text-foreground">
      {{ t('errors.boundary.title') }}
    </h2>
    <p class="max-w-md text-sm text-muted-foreground">
      {{ isChunkLoadError(error) ? t('errors.boundary.chunkMessage') : t('errors.boundary.message') }}
    </p>
    <div class="flex gap-2">
      <button
        v-if="isChunkLoadError(error)"
        class="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        @click="reload"
      >
        {{ t('errors.boundary.reload') }}
      </button>
      <button
        v-else
        class="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        @click="reset"
      >
        {{ t('errors.boundary.retry') }}
      </button>
    </div>
  </div>
</template>
